// Submenú "Conciliación" del módulo Control Bancario.
//   Conciliación automática → motor de emparejamiento por reglas
//   Conciliación manual      → emparejar/crear/desconciliar a mano
//   Partidas conciliatorias  → items sin conciliar + cierre
//   Historial                → cierres realizados
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight, CheckCircle2, GitCompare, History, ListChecks, Loader2, Lock, Plus, Sparkles, Unlink,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import {
  CATEGORIA_MOV, Campo, CuentaMini, EstadoConcil, Modal, cb, cbRpc, fieldCls, hoyISO, money,
} from './shared';

// selector de cuenta reutilizable
function useCuentas() {
  return useQuery<CuentaMini[]>({
    queryKey: ['cb-cuentas-mini'],
    queryFn: () => cb.list<CuentaMini>('cuentas', '?select=id,alias,numero,moneda,banco:banco_id(nombre)&deleted_at=is.null&es_activo=is.true&order=alias,numero'),
  });
}
function useEstado(cuentaId: string, hasta: string) {
  return useQuery<EstadoConcil>({
    queryKey: ['cb-concil-estado', cuentaId, hasta],
    queryFn: () => cbRpc<EstadoConcil>('cb_conciliacion_estado', { _cuenta_id: cuentaId, _hasta: hasta || null }),
    enabled: !!cuentaId,
  });
}
function CuentaSelect({ cuentas, value, onChange }: { cuentas: CuentaMini[]; value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation('bancos');
  return (
    <select className={fieldCls} value={value} onChange={(e) => onChange(e.target.value)}>
      {cuentas.length === 0 && <option value="">{t('concil.sin_cuentas')}</option>}
      {cuentas.map((c) => <option key={c.id} value={c.id}>{c.banco?.nombre} · {c.alias ?? c.numero} ({c.moneda})</option>)}
    </select>
  );
}
function Kpi({ label: l, valor, clase, fuerte }: { label: string; valor: string | number; clase?: string; fuerte?: boolean }) {
  return (
    <Card><CardContent className="py-3">
      <div className="text-xs text-muted-foreground">{l}</div>
      <div className={`font-mono tabular-nums ${fuerte ? 'text-lg font-semibold' : 'text-base'} ${clase ?? ''}`}>{valor}</div>
    </CardContent></Card>
  );
}
const invalidarTodo = (qc: ReturnType<typeof useQueryClient>) => {
  ['cb-concil-estado', 'cb-conciliaciones', 'cb-libro', 'cb-posicion'].forEach((k) => void qc.invalidateQueries({ queryKey: [k] }));
};

// tabla de pares ya emparejados (compartida por automática y manual)
function TablaEmparejados({ est, moneda, onDes }: { est?: EstadoConcil; moneda: string; onDes: (id: string) => void }) {
  const { t } = useTranslation('bancos');
  const rows = est?.emparejados ?? [];
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">{t('concil.emparejados', { n: rows.length })}</CardTitle></CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2 pr-3">{t('concil.col_fecha')}</th><th className="py-2 pr-3">{t('concil.col_extracto')}</th><th className="py-2 pr-3">{t('concil.col_libro')}</th><th className="py-2 pr-3">{t('concil.col_tipo')}</th><th className="py-2 pr-3 text-right">{t('concil.col_monto')}</th><th className="py-2"></th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">{t('concil.sin_emparejamientos')}</td></tr>}
            {rows.map((e) => (
              <tr key={e.linea_id} className="border-b last:border-0 hover:bg-muted/40">
                <td className="py-2 pr-3 whitespace-nowrap">{e.fecha}</td>
                <td className="py-2 pr-3">{e.concepto ?? '—'} {e.referencia && <span className="font-mono text-xs text-muted-foreground">({e.referencia})</span>}</td>
                <td className="py-2 pr-3">{e.mov_concepto ?? '—'} {e.mov_origen === 'ajuste' && <span className="text-xs text-muted-foreground">{t('concil.registrado_desde_extracto')}</span>}</td>
                <td className="py-2 pr-3 capitalize">{t(`concil.tipo.${e.tipo}`)}</td>
                <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(e.monto, moneda)}</td>
                <td className="py-2 text-right">
                  <button className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" onClick={() => onDes(e.linea_id)}><Unlink className="w-3.5 h-3.5" /> {t('concil.btn_desconciliar')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

// ==================================================== AUTOMÁTICA ==============
export function ConciliacionAutomatica() {
  const { t } = useTranslation('bancos');
  const qc = useQueryClient();
  const { data: cuentas = [] } = useCuentas();
  const [cuentaId, setCuentaId] = useState('');
  const cta = cuentaId || cuentas[0]?.id || '';
  const [hasta, setHasta] = useState('');
  const { data: est, isFetching } = useEstado(cta, hasta);
  const moneda = est?.cuenta.moneda ?? 'USD';

  const auto = useMutation({
    mutationFn: () => cbRpc<{ conciliados: number; pendientes: number }>('cb_conciliar_auto', { _cuenta_id: cta, _hasta: hasta || null }),
    onSuccess: (r) => { toast.success(t('auto.toast_ok', { conciliados: r.conciliados, pendientes: r.pendientes })); invalidarTodo(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const des = useMutation({
    mutationFn: (id: string) => cbRpc('cb_desconciliar', { _linea_id: id }),
    onSuccess: () => { toast.success(t('concil.desconciliado')); invalidarTodo(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><Sparkles className="w-5 h-5" /> {t('auto.titulo')}</h1>
      <Card><CardContent className="py-3 flex flex-wrap items-end gap-3">
        <Campo label={t('auto.f_cuenta')} className="min-w-[220px]"><CuentaSelect cuentas={cuentas} value={cta} onChange={setCuentaId} /></Campo>
        <Campo label={t('auto.f_hasta')}><input type="date" className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
        <Button isLoading={auto.isPending} disabled={!cta} onClick={() => auto.mutate()}><Sparkles className="w-4 h-4 mr-1" /> {t('auto.btn_ejecutar')}</Button>
      </CardContent></Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label={t('auto.kpi_mov')} valor={est?.totales.mov_pendientes ?? 0} />
        <Kpi label={t('auto.kpi_lin')} valor={est?.totales.lin_pendientes ?? 0} />
        <Kpi label={t('auto.kpi_emp')} valor={est?.totales.emparejados ?? 0} clase="text-estable" />
        <Kpi label={t('auto.kpi_dif')} valor={money(est?.diferencia, moneda)} fuerte clase={est && Math.abs(est.diferencia) > 0.005 ? 'text-pendiente' : 'text-estable'} />
      </div>

      {isFetching && <div className="py-2 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-primary" /></div>}
      <p className="text-sm text-muted-foreground">{t('auto.ayuda')}</p>
      <TablaEmparejados est={est} moneda={moneda} onDes={des.mutate} />
    </div>
  );
}

// ==================================================== MANUAL ==================
export function ConciliacionManual() {
  const { t } = useTranslation('bancos');
  const qc = useQueryClient();
  const { data: cuentas = [] } = useCuentas();
  const [cuentaId, setCuentaId] = useState('');
  const cta = cuentaId || cuentas[0]?.id || '';
  const { data: est, isFetching } = useEstado(cta, '');
  const moneda = est?.cuenta.moneda ?? 'USD';

  const [selMov, setSelMov] = useState('');
  const [selLin, setSelLin] = useState('');
  const [crearLin, setCrearLin] = useState<string | null>(null);
  const [categoria, setCategoria] = useState('');

  const emparejar = useMutation({
    mutationFn: () => cbRpc('cb_conciliar_manual', { _movimiento_id: selMov, _linea_id: selLin }),
    onSuccess: () => { toast.success(t('manual.emparejados_ok')); setSelMov(''); setSelLin(''); invalidarTodo(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const crear = useMutation({
    mutationFn: () => cbRpc('cb_crear_mov_desde_linea', { _linea_id: crearLin, _categoria: categoria || null }),
    onSuccess: () => { toast.success(t('manual.creado_ok')); setCrearLin(null); setCategoria(''); invalidarTodo(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const des = useMutation({
    mutationFn: (id: string) => cbRpc('cb_desconciliar', { _linea_id: id }),
    onSuccess: () => { toast.success(t('concil.desconciliado')); invalidarTodo(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const movs = est?.movimientos_pendientes ?? [];
  const lins = est?.lineas_pendientes ?? [];

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><ListChecks className="w-5 h-5" /> {t('manual.titulo')}</h1>
      <Card><CardContent className="py-3 flex flex-wrap items-end gap-3">
        <Campo label={t('auto.f_cuenta')} className="min-w-[220px]"><CuentaSelect cuentas={cuentas} value={cta} onChange={setCuentaId} /></Campo>
        <Button disabled={!selMov || !selLin} isLoading={emparejar.isPending} onClick={() => emparejar.mutate()}><ArrowRight className="w-4 h-4 mr-1" /> {t('manual.btn_emparejar')}</Button>
        {isFetching && <Loader2 className="w-5 h-5 animate-spin text-primary" />}
      </CardContent></Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Libro */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">{t('manual.libro_titulo', { n: movs.length })}</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto max-h-[420px]">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground"><th></th><th className="py-2 pr-3">{t('concil.col_fecha')}</th><th className="py-2 pr-3">{t('concil.col_concepto')}</th><th className="py-2 pr-3">{t('concil.col_tipo')}</th><th className="py-2 pr-3 text-right">{t('concil.col_monto')}</th></tr></thead>
              <tbody>
                {movs.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">{t('manual.todo_conciliado')}</td></tr>}
                {movs.map((m) => (
                  <tr key={m.id} className={`border-b last:border-0 cursor-pointer ${selMov === m.id ? 'bg-primary/10' : 'hover:bg-muted/40'}`} onClick={() => setSelMov(m.id)}>
                    <td className="py-2 pl-2 pr-1"><input type="radio" checked={selMov === m.id} onChange={() => setSelMov(m.id)} /></td>
                    <td className="py-2 pr-3 whitespace-nowrap">{m.fecha}</td>
                    <td className="py-2 pr-3">{m.concepto ?? '—'} {m.referencia && <span className="font-mono text-xs text-muted-foreground">({m.referencia})</span>}</td>
                    <td className="py-2 pr-3 capitalize">{t(`concil.tipo.${m.tipo}`)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(m.monto, moneda)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
        {/* Extracto */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">{t('manual.extracto_titulo', { n: lins.length })}</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto max-h-[420px]">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground"><th></th><th className="py-2 pr-3">{t('concil.col_fecha')}</th><th className="py-2 pr-3">{t('concil.col_concepto')}</th><th className="py-2 pr-3">{t('concil.col_tipo')}</th><th className="py-2 pr-3 text-right">{t('concil.col_monto')}</th><th></th></tr></thead>
              <tbody>
                {lins.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">{t('manual.sin_lineas')}</td></tr>}
                {lins.map((l) => (
                  <tr key={l.id} className={`border-b last:border-0 ${selLin === l.id ? 'bg-primary/10' : 'hover:bg-muted/40'}`}>
                    <td className="py-2 pl-2 pr-1"><input type="radio" checked={selLin === l.id} onChange={() => setSelLin(l.id)} /></td>
                    <td className="py-2 pr-3 whitespace-nowrap cursor-pointer" onClick={() => setSelLin(l.id)}>{l.fecha}</td>
                    <td className="py-2 pr-3 cursor-pointer" onClick={() => setSelLin(l.id)}>{l.concepto ?? '—'} {l.referencia && <span className="font-mono text-xs text-muted-foreground">({l.referencia})</span>}</td>
                    <td className="py-2 pr-3 capitalize cursor-pointer" onClick={() => setSelLin(l.id)}>{t(`concil.tipo.${l.tipo}`)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums cursor-pointer" onClick={() => setSelLin(l.id)}>{money(l.monto, moneda)}</td>
                    <td className="py-2 pr-2 text-right"><button className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs hover:bg-muted" title={t('manual.tt_registrar')} onClick={() => setCrearLin(l.id)}><Plus className="w-3.5 h-3.5" /> {t('manual.btn_registrar')}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      <TablaEmparejados est={est} moneda={moneda} onDes={des.mutate} />

      {crearLin && (
        <Modal title={t('manual.modal_titulo')} onClose={() => setCrearLin(null)}>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{t('manual.modal_ayuda')}</p>
            <Campo label={t('manual.f_categoria')}>
              <select className={fieldCls} value={categoria} onChange={(e) => setCategoria(e.target.value)}>
                <option value="">{t('manual.sin_categoria')}</option>
                {CATEGORIA_MOV.filter((c) => c.v !== 'transferencia').map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
              </select>
            </Campo>
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setCrearLin(null)}>{t('manual.cancelar')}</Button>
              <Button isLoading={crear.isPending} onClick={() => crear.mutate()}>{t('manual.btn_registrar_conciliar')}</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ==================================================== PARTIDAS ================
export function Partidas() {
  const qc = useQueryClient();
  const { data: cuentas = [] } = useCuentas();
  const [cuentaId, setCuentaId] = useState('');
  const cta = cuentaId || cuentas[0]?.id || '';
  const [corte, setCorte] = useState(hoyISO());
  const { data: est, isFetching } = useEstado(cta, corte);
  const moneda = est?.cuenta.moneda ?? 'USD';

  const [cerrar, setCerrar] = useState(false);
  const [saldoExt, setSaldoExt] = useState(0);
  const [notas, setNotas] = useState('');

  const abrirCierre = () => { setSaldoExt(est?.saldo_extracto ?? 0); setNotas(''); setCerrar(true); };
  const cerrarM = useMutation({
    mutationFn: () => cbRpc('cb_conciliacion_cerrar', { _cuenta_id: cta, _fecha_corte: corte, _saldo_extracto: Number(saldoExt), _notas: notas || null }),
    onSuccess: (r: any) => { toast.success(`Conciliación cerrada · diferencia ${money(r.diferencia, moneda)}`); setCerrar(false); invalidarTodo(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const movs = est?.movimientos_pendientes ?? [];
  const lins = est?.lineas_pendientes ?? [];
  const conciliable = !!est && est.totales.mov_pendientes === 0 && est.totales.lin_pendientes === 0;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-xl font-semibold flex items-center gap-2"><GitCompare className="w-5 h-5" /> Partidas conciliatorias</h1>
        <Button onClick={abrirCierre} disabled={!cta}><Lock className="w-4 h-4 mr-1" /> Cerrar conciliación</Button>
      </div>
      <Card><CardContent className="py-3 flex flex-wrap items-end gap-3">
        <Campo label="Cuenta" className="min-w-[220px]"><CuentaSelect cuentas={cuentas} value={cta} onChange={setCuentaId} /></Campo>
        <Campo label="Fecha de corte"><input type="date" className={fieldCls} value={corte} onChange={(e) => setCorte(e.target.value)} /></Campo>
        {isFetching && <Loader2 className="w-5 h-5 animate-spin text-primary" />}
      </CardContent></Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Kpi label="Saldo según libros" valor={money(est?.saldo_libro, moneda)} fuerte />
        <Kpi label="Saldo según extracto" valor={money(est?.saldo_extracto, moneda)} fuerte />
        <Kpi label="Diferencia" valor={money(est?.diferencia, moneda)} fuerte clase={est && Math.abs(est.diferencia) > 0.005 ? 'text-pendiente' : 'text-estable'} />
      </div>
      {conciliable && <div className="flex items-center gap-2 rounded-md bg-estable-soft text-estable px-3 py-2 text-sm"><CheckCircle2 className="w-4 h-4" /> Sin partidas pendientes: la cuenta está lista para cerrar.</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">En libro, no en el extracto ({movs.length})</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <p className="pb-2 text-xs text-muted-foreground">Cheques girados no cobrados, depósitos en tránsito…</p>
            <PartTabla rows={movs} moneda={moneda} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">En el extracto, no en libro ({lins.length})</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <p className="pb-2 text-xs text-muted-foreground">Comisiones, intereses, notas de débito/crédito…</p>
            <PartTabla rows={lins} moneda={moneda} />
          </CardContent>
        </Card>
      </div>

      {cerrar && (
        <Modal title="Cerrar conciliación" onClose={() => setCerrar(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Fecha de corte"><input type="date" className={fieldCls} value={corte} onChange={(e) => setCorte(e.target.value)} /></Campo>
              <Campo label={`Saldo del extracto (${moneda})`}><input type="number" step="0.01" className={fieldCls} value={saldoExt} onChange={(e) => setSaldoExt(Number(e.target.value))} /></Campo>
            </div>
            <div className="rounded-md border p-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Saldo según libros</span><span className="font-mono">{money(est?.saldo_libro, moneda)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Saldo del extracto (ingresado)</span><span className="font-mono">{money(saldoExt, moneda)}</span></div>
              <div className="flex justify-between border-t pt-1 font-semibold"><span>Diferencia</span><span className={`font-mono ${Math.abs((est?.saldo_libro ?? 0) - saldoExt) > 0.005 ? 'text-pendiente' : 'text-estable'}`}>{money((est?.saldo_libro ?? 0) - saldoExt, moneda)}</span></div>
            </div>
            {!conciliable && <p className="text-xs text-pendiente">Aún hay partidas pendientes; puedes cerrar igual, pero la diferencia quedará registrada.</p>}
            <Campo label="Notas"><textarea className={`${fieldCls} h-16`} value={notas} onChange={(e) => setNotas(e.target.value)} /></Campo>
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setCerrar(false)}>Cancelar</Button>
              <Button isLoading={cerrarM.isPending} onClick={() => cerrarM.mutate()}>Confirmar cierre</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
function PartTabla({ rows, moneda }: { rows: { id: string; fecha: string; tipo: string; monto: number; concepto: string | null; referencia: string | null }[]; moneda: string }) {
  return (
    <table className="w-full text-sm">
      <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Concepto</th><th className="py-2 pr-3">Tipo</th><th className="py-2 pr-3 text-right">Monto</th></tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-muted-foreground">Sin partidas</td></tr>}
        {rows.map((r) => (
          <tr key={r.id} className="border-b last:border-0">
            <td className="py-2 pr-3 whitespace-nowrap">{r.fecha}</td>
            <td className="py-2 pr-3">{r.concepto ?? '—'} {r.referencia && <span className="font-mono text-xs text-muted-foreground">({r.referencia})</span>}</td>
            <td className="py-2 pr-3 capitalize">{r.tipo}</td>
            <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(r.monto, moneda)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ==================================================== HISTORIAL ===============
interface Cierre { id: string; fecha_corte: string; cuenta: string; moneda: string; saldo_extracto: number; saldo_libro: number; diferencia: number; n_movimientos: number; n_lineas: number; notas: string | null; creado: string; }
export function HistorialConciliaciones() {
  const { data: rows = [], isFetching } = useQuery<Cierre[]>({
    queryKey: ['cb-conciliaciones'],
    queryFn: () => cbRpc<Cierre[]>('cb_conciliaciones'),
  });
  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><History className="w-5 h-5" /> Conciliaciones cerradas</h1>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Cierres ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Corte</th><th className="py-2 pr-3">Cuenta</th><th className="py-2 pr-3 text-right">Saldo libro</th><th className="py-2 pr-3 text-right">Saldo extracto</th><th className="py-2 pr-3 text-right">Diferencia</th><th className="py-2 pr-3 text-right">Movs.</th><th className="py-2 pr-3 text-right">Líneas</th><th className="py-2 pr-3">Notas</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">Sin conciliaciones cerradas</td></tr>}
              {rows.map((c) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 whitespace-nowrap">{c.fecha_corte}</td>
                  <td className="py-2 pr-3">{c.cuenta}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(c.saldo_libro, c.moneda)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(c.saldo_extracto, c.moneda)}</td>
                  <td className={`py-2 pr-3 text-right font-mono tabular-nums ${Math.abs(c.diferencia) > 0.005 ? 'text-pendiente' : 'text-estable'}`}>{money(c.diferencia, c.moneda)}</td>
                  <td className="py-2 pr-3 text-right font-mono">{c.n_movimientos}</td>
                  <td className="py-2 pr-3 text-right font-mono">{c.n_lineas}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{c.notas ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
